/**
 * Purchase Order Creation Hook
 * 
 * Orchestrates the complete purchase order creation workflow via server-side edge function.
 * Handles payment processing, milestone creation, and all database updates in a reliable,
 * atomic transaction with proper error handling and rollback capabilities.
 * 
 * **PO Creation Workflow:**
 * 1. Validate milestone approval status (client-side quick check)
 * 2. Estimate payment amount for UI feedback
 * 3. Invoke edge function for server-side orchestration
 * 4. Handle payment processing (immediate or ACH pending)
 * 5. Create PO record with generated PO number
 * 6. Create financial reconciliation record
 * 7. Create anchor milestones if requested
 * 8. Update job status to 'awarded'
 * 9. Send notifications to vendor
 * 
 * **Payment Processing:**
 * - Credit/Debit cards: Immediate processing
 * - ACH: Pending status with 5-day settlement
 * - Supports callbacks for UI updates at each stage
 * - Escrow model: funds held until job completion
 * 
 * **Milestone Creation:**
 * - Optional anchor milestones (job start, job complete)
 * - Configurable via includeMilestones parameter
 * - Automatic date calculation based on job timeline
 * - Action owner assignment (client vs vendor)
 * 
 * **Error Handling:**
 * - Comprehensive error tracking via Sentry
 * - Detailed logging with correlation IDs
 * - User-friendly error messages
 * - Automatic rollback on failure
 * - Edge function error reporting
 * 
 * **Idempotency:**
 * - Uses jobId as idempotency key
 * - Prevents duplicate PO creation
 * - Safe to retry on network errors
 * - Server-side deduplication
 * 
 * **Key Features:**
 * - Server-side orchestration for reliability
 * - Payment processing with multiple methods
 * - ACH pending state handling
 * - Optional milestone creation
 * - Progress callbacks for UI updates
 * - Comprehensive error tracking
 * - Idempotent operations
 * - Correlation ID tracking
 * 
 * **Use Cases:**
 * - Award acceptance with payment
 * - PO generation after bid award
 * - Milestone-based job initiation
 * - Escrow fund establishment
 * - Financial audit trail creation
 * 
 * @returns {Object} PO creation functions and state
 * @returns {Function} createPurchaseOrder - Create PO with payment
 * @returns {boolean} loading - Creation in progress
 * 
 * @example
 * // Create PO with milestone setup
 * const { createPurchaseOrder, loading } = useCreatePurchaseOrder();
 * 
 * const handleAcceptAward = async (jobId: string, winningBidId: string) => {
 *   try {
 *     const result = await createPurchaseOrder(
 *       jobId,
 *       winningBidId,
 *       true, // Include anchor milestones
 *       {
 *         onPaymentStart: (amount) => {
 *           console.log(`Processing payment: $${amount / 100}`);
 *           setPaymentStep('processing');
 *         },
 *         onPaymentSuccess: (transactionId) => {
 *           console.log('Payment successful:', transactionId);
 *           setPaymentStep('complete');
 *         },
 *         onAchPending: (settlementEta) => {
 *           console.log('ACH pending, settles:', settlementEta);
 *           setPaymentStep('ach_pending');
 *         },
 *         onPaymentError: (error) => {
 *           console.error('Payment failed:', error);
 *           setPaymentStep('error');
 *         }
 *       }
 *     );
 *     
 *     if (result.achPending) {
 *       navigate(`/jobs/${jobId}/ach-pending`);
 *     } else if (result.purchaseOrder) {
 *       navigate(`/purchase-orders/${result.purchaseOrder.id}`);
 *     }
 *   } catch (error) {
 *     console.error('PO creation failed:', error);
 *   }
 * };
 * 
 * @example
 * // Simple PO creation without milestones
 * const { createPurchaseOrder } = useCreatePurchaseOrder();
 * 
 * const handleQuickPO = async () => {
 *   const { purchaseOrder } = await createPurchaseOrder(
 *     jobId,
 *     winningBidId,
 *     false // No milestones
 *   );
 *   
 *   toast({ 
 *     title: 'PO Created', 
 *     description: `PO ${purchaseOrder.po_number} created successfully` 
 *   });
 * };
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { errorTracking } from '@/lib/errorTracking';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export const useCreatePurchaseOrder = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createPurchaseOrder = async (
    jobId: string, 
    winningBidId: string, 
    includeMilestones = false,
    callbacks?: {
      selectedPaymentMethodId?: string;
      onPaymentStart?: (amount: number) => void;
      onPaymentSuccess?: (transactionId: string) => void;
      onPaymentError?: (error: string, details?: {
        recommendation?: string;
        code?: string;
        canRetry?: boolean;
        suggestAch?: boolean;
        suggestUpdatePayment?: boolean;
        severity?: 'temporary' | 'permanent' | 'requires_action';
      }) => void;
      onAchPending?: (settlementEta: string) => void;
      onInvoicePending?: (invoice: { invoiceId: string; invoiceNumber: string; invoiceUrl: string; pdfUrl?: string }) => void;
    }
  ) => {
    const correlationId = jobId;
    console.log(`[PO Hook][${correlationId}] Starting PO creation`);
    
    try {
      setLoading(true);
      
      // Quick client-side validation
      const { data: milestoneApproval, error: approvalError } = await supabase
        .from('milestone_approvals')
        .select('approval_status')
        .eq('job_id', jobId)
        .single();

      if (approvalError && approvalError.code !== 'PGRST116') throw approvalError;
      if (!milestoneApproval || milestoneApproval.approval_status !== 'approved_by_both') {
        throw new Error('Milestone approval required from both parties');
      }

      // Estimate amount for callback (including tax)
      if (callbacks?.onPaymentStart) {
        const { data: award } = await supabase.from('awards')
          .select('id, total_recalculated_tax_cents')
          .eq('winning_bid_id', winningBidId).single();
        const { data: selections } = await supabase.from('award_selections')
          .select('total_price_cents, bid_line_item_id')
          .eq('award_id', award?.id || '');
        const subtotal = selections?.reduce((sum, s) => sum + s.total_price_cents, 0) || 0;
        
        // Include recalculated tax if available, otherwise sum tax from awarded line items only
        let taxAmount = award?.total_recalculated_tax_cents || 0;
        if (!taxAmount && selections && selections.length > 0) {
          const awardedLineItemIds = selections
            .map(s => s.bid_line_item_id)
            .filter(Boolean);
          
          if (awardedLineItemIds.length > 0) {
            const { data: taxCalcs } = await supabase
              .from('bid_line_item_tax_calculations')
              .select('vendor_tax_cents')
              .in('bid_line_item_id', awardedLineItemIds);
            
            taxAmount = taxCalcs?.reduce((sum, t) => sum + (t.vendor_tax_cents || 0), 0) || 0;
          }
        }
        
        const total = subtotal + taxAmount;
        callbacks.onPaymentStart(total);
      }

      // Call server orchestration with PO creation intent for ACH
      const { data: result, error: serverError } = await invokeEdgeFunction('accept-and-issue-po', {
        body: { 
          jobId, 
          winningBidId, 
          includeMilestones, 
          selectedPaymentMethodId: callbacks?.selectedPaymentMethodId,
          idempotencyKey: jobId,
          poCreationIntent: {
            jobId,
            winningBidId,
            includeMilestones,
            selectedPaymentMethodId: callbacks?.selectedPaymentMethodId
          }
        },
        callerOperation: 'useCreatePurchaseOrder.create',
      });

      // Type the result from edge function
      interface POCreationResult {
        status: 'success' | 'ach_pending' | 'invoice_pending';
        correlationId?: string;
        settlementEta?: string;
        transactionId?: string;
        invoiceId?: string;
        invoiceNumber?: string;
        invoiceUrl?: string;
        pdfUrl?: string;
        po?: {
          id: string;
          po_number: string;
          total_amount_cents: number;
        };
      }

      if (serverError) {
        console.error(`[PO Hook][${correlationId}] Server error:`, serverError);
        
        // Try to extract structured error details from the edge function response
        let errorMessage = serverError.message || 'Failed to create PO';
        let errorDetails: {
          recommendation?: string;
          code?: string;
          canRetry?: boolean;
          suggestAch?: boolean;
          suggestUpdatePayment?: boolean;
          severity?: 'temporary' | 'permanent' | 'requires_action';
        } = {};
        
        // Edge function errors may contain structured context (EdgeFunctionError from our wrapper)
        const errAny = serverError as { context?: string | Record<string, unknown> };
        if (errAny.context) {
          try {
            const ctx = typeof errAny.context === 'string' 
              ? JSON.parse(errAny.context) 
              : errAny.context;
            
            // Extract user-friendly error message if available
            if (ctx.error) {
              errorMessage = ctx.error;
            }
            
            // Extract structured error details
            const validSeverities = ['temporary', 'permanent', 'requires_action'] as const;
            const parsedSeverity = validSeverities.includes(ctx.severity) 
              ? ctx.severity as 'temporary' | 'permanent' | 'requires_action'
              : undefined;
            
            errorDetails = {
              recommendation: ctx.recommendation,
              code: ctx.code,
              canRetry: ctx.canRetry,
              suggestAch: ctx.suggestAch,
              suggestUpdatePayment: ctx.suggestUpdatePayment,
              severity: parsedSeverity,
            };
          } catch {
            // If parsing fails, use the raw message
            console.warn('[PO Hook] Failed to parse error context');
          }
        }
        
        // Report detailed edge function errors to Sentry
        errorTracking.captureCustomError(
          `PO Creation Failed: ${errorMessage}`,
          'high',
          { 
            jobId, 
            winningBidId, 
            correlationId,
            errorDetails: serverError,
            parsedErrorDetails: errorDetails,
            component: 'useCreatePurchaseOrder',
            edgeFunction: 'accept-and-issue-po'
          }
        );
        
        callbacks?.onPaymentError?.(errorMessage, errorDetails);
        throw new Error(errorMessage);
      }

      const poResult = result as POCreationResult;

      // Log server correlationId for supportability
      if (poResult?.correlationId) {
        console.log(`[PO Hook][${correlationId}] Server trace ID: ${poResult.correlationId}`);
      }

      // Handle ACH pending
      if (poResult.status === 'ach_pending') {
        console.log(`[PO Hook][${correlationId}] ACH payment pending, settlementEta:`, poResult.settlementEta);
        callbacks?.onAchPending?.(poResult.settlementEta || '');
        toast({ title: 'ACH Payment Pending', description: 'PO will be created after settlement (~5 business days)' });
        return { purchaseOrder: null, achPending: true, settlementEta: poResult.settlementEta, transactionId: poResult.transactionId, correlationId: poResult.correlationId };
      }

      // Handle Invoice pending
      if (poResult.status === 'invoice_pending') {
        console.log(`[PO Hook][${correlationId}] Invoice payment pending:`, {
          invoiceNumber: poResult.invoiceNumber,
          invoiceUrl: poResult.invoiceUrl
        });
        callbacks?.onInvoicePending?.({
          invoiceId: poResult.invoiceId || '',
          invoiceNumber: poResult.invoiceNumber || '',
          invoiceUrl: poResult.invoiceUrl || '',
          pdfUrl: poResult.pdfUrl,
        });
        toast({ title: 'Invoice Sent', description: 'PO will be created after invoice is paid.' });
        return { 
          purchaseOrder: null, 
          invoicePending: true, 
          invoiceId: poResult.invoiceId,
          invoiceNumber: poResult.invoiceNumber,
          invoiceUrl: poResult.invoiceUrl,
          pdfUrl: poResult.pdfUrl,
          correlationId: poResult.correlationId 
        };
      }

      // Handle success
      if (poResult.status === 'success' && poResult.po) {
        console.log(`[PO Hook][${correlationId}] PO created successfully:`, poResult.po.po_number);
        
        // Track PO generation and payment
        analytics.track({
          name: ANALYTICS_EVENTS.PO_GENERATED,
          category: 'transaction',
          properties: {
            job_id: jobId,
            po_id: poResult.po.id,
            amount_cents: poResult.po.total_amount_cents,
          },
        });
        
        analytics.track({
          name: ANALYTICS_EVENTS.PAYMENT_COMPLETED,
          category: 'transaction',
          properties: {
            job_id: jobId,
            amount_cents: poResult.po.total_amount_cents,
            payment_method: callbacks?.selectedPaymentMethodId ? 'saved' : 'default',
          },
        });

        callbacks?.onPaymentSuccess?.(poResult.transactionId || '');
        toast({ title: 'Success', description: `Purchase Order ${poResult.po.po_number} created!` });
        return { purchaseOrder: poResult.po, transactionId: poResult.transactionId, correlationId: poResult.correlationId };
      }

      throw new Error(`Unexpected status: ${poResult.status}`);

    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[PO Hook][${correlationId}] Error:`, msg, error);
      
      // Ensure error is reported to Sentry with full context
      errorTracking.captureCustomError(
        `Purchase Order Creation Error: ${msg}`,
        'high',
        { 
          jobId, 
          winningBidId, 
          correlationId,
          errorObject: error,
          stackTrace: error instanceof Error ? error.stack : undefined,
          component: 'useCreatePurchaseOrder'
        }
      );
      
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createPurchaseOrder, loading };
};
